/**
 * Maps a slideshow's sound-timeline segments to absolute playback windows so audio can be
 * synced to the slide timeline's playhead.
 *
 * Each segment's `slideshowSegmentStart` is an absolute millisecond timestamp on the slideshow
 * timeline. The song then occupies
 * `[slideshowSegmentStart, slideshowSegmentStart + (endMs - startMs))`, where the length is the
 * trimmed duration. Trimming is not editable yet (FOR-16165), so today `startMs` is 0 and
 * `endMs` is the full song duration. Segments may leave silent gaps between them.
 */

export interface ISongPlaybackWindow {
    songId: string;
    /** Inclusive start of this song on the slideshow timeline (ms). */
    timelineStartMs: number;
    /** Exclusive end of this song on the slideshow timeline (ms). */
    timelineEndMs: number;
    /** Offset into the song where playback begins (ms) — the trim start. */
    trimStartMs: number;
    /** Pre-resolved stream URL embedded on the segment, if any — see ISlideshowSongSegment. */
    streamUrl?: string | null;
}

type SongSegmentInput = Pick<
    SpApi.ISlideshowSongSegment,
    'songId' | 'slideshowSegmentStart' | 'startMs' | 'endMs' | 'streamUrl'
>;

function getSegmentTrimDurationMs(segment: SongSegmentInput): number {
    const trimStartMs = Math.max(0, segment.startMs ?? 0);
    const trimEndMs = Math.max(trimStartMs, segment.endMs ?? 0);

    return trimEndMs - trimStartMs;
}

/**
 * Builds absolute playback windows from sound-timeline segments.
 * Segments without a positive trimmed duration or a song id are skipped. Windows are sorted by
 * timeline start so lookups stay deterministic when segments are stored out of order.
 */
export function buildSongPlaybackWindows(segments: SongSegmentInput[]): ISongPlaybackWindow[] {
    const windows: ISongPlaybackWindow[] = [];

    segments.forEach((segment) => {
        const durationMs = getSegmentTrimDurationMs(segment);

        if (!segment.songId || durationMs <= 0) {
            return;
        }

        const timelineStartMs = Math.max(0, segment.slideshowSegmentStart ?? 0);

        windows.push({
            songId: String(segment.songId),
            timelineStartMs,
            timelineEndMs: timelineStartMs + durationMs,
            trimStartMs: Math.max(0, segment.startMs ?? 0),
            streamUrl: segment.streamUrl ?? null
        });
    });

    return windows.sort((a, b) => a.timelineStartMs - b.timelineStartMs);
}

/**
 * Returns the playback window active at the given timeline position, or null when the playhead
 * sits before the first song, in a gap, or past the last song.
 */
export function findActiveSongWindow(
    windows: ISongPlaybackWindow[],
    timelineMs: number
): ISongPlaybackWindow | null {
    return (
        windows.find(
            (window) => timelineMs >= window.timelineStartMs && timelineMs < window.timelineEndMs
        ) ?? null
    );
}

/**
 * The position (seconds) the song's audio element should be at for a given timeline position,
 * i.e. the trim start plus how far the playhead has advanced into this song's window.
 */
export function getSongPlaybackOffsetSeconds(
    window: ISongPlaybackWindow,
    timelineMs: number
): number {
    return (window.trimStartMs + (timelineMs - window.timelineStartMs)) / 1000;
}

/**
 * End of the soundtrack on the slideshow timeline (ms) — the latest segment end, accounting for
 * each segment's absolute `slideshowSegmentStart`.
 */
export function getSongSegmentsDurationMs(segments: SongSegmentInput[] | null | undefined): number {
    const windows = buildSongPlaybackWindows(segments ?? []);

    if (windows.length === 0) {
        return 0;
    }

    return windows.reduce((maxEnd, window) => Math.max(maxEnd, window.timelineEndMs), 0);
}

/**
 * Next free timestamp for appending a song to the sound row: 0 when empty, otherwise the end of
 * the latest segment so newly added songs still abut the previous one by default.
 */
export function getNextSongSegmentStartMs(segments: SongSegmentInput[] | null | undefined): number {
    return getSongSegmentsDurationMs(segments);
}

/**
 * Playable length of a slideshow: long enough to cover both the slide content and the
 * soundtrack. When music runs past the last slide, playback continues with an empty frame
 * (see SlideshowPlayer) until the music ends.
 */
export function getSlideshowPlaybackDurationMs(
    slideContentDurationMs: number,
    songSegments: SongSegmentInput[] | null | undefined
): number {
    return Math.max(slideContentDurationMs, getSongSegmentsDurationMs(songSegments));
}
